import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import { Button } from "@faire/slate/components/button";
import { ProgressBar } from "@faire/slate/components/progress-bar";
import { Link } from "react-router-dom";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";
import { cn } from "../../lib/utils";

export interface BrandSideCartItem {
  token: string;
  name: string;
  imageUrl: string;
  /** Per-unit wholesale price (numeric). Line total is unitPrice × quantity. */
  unitPrice: number;
  quantity: number;
  /** Shows an action that returns this active line to saved for later. */
  canMoveToSavedForLater?: boolean;
}

export interface BrandSideCartRecommendation {
  token: string;
  name: string;
  imageUrl: string;
  /** Formatted wholesale price (e.g. "$12"). */
  price: string;
  /** Formatted MSRP (e.g. "MSRP $24"). */
  msrp?: string;
  /** Optional eyebrow label shown above the name (e.g. "Bestseller"). */
  badge?: string;
}

export interface BrandSideCartProps {
  brandName: string;
  /** Order minimum in dollars (e.g. 150). */
  minimum: number;
  items: BrandSideCartItem[];
  recommendations?: BrandSideCartRecommendation[];
  /** Sales-tax rate applied to the item total. Defaults to 0.06. */
  taxRate?: number;
  onChangeQuantity?: (token: string, quantity: number) => void;
  onRemove?: (token: string) => void;
  onMoveToSavedForLater?: (token: string) => void;
  onAddRecommendation?: (token: string) => void;
  onViewCart?: () => void;
  onCheckout?: () => void;
  className?: string;
  /** When provided, shows a back icon button to the left of the brand name (e.g. to return to a multi-brand cart list). */
  onBack?: () => void;
  /** When provided, shows a close icon button at the top-right of the header. */
  onClose?: () => void;
  /**
   * Token of the line to tint — used to point at the item that was just added.
   * The tint fades out on its own when the caller clears this.
   */
  highlightedItemToken?: string;
  /**
   * Tokens of the lines to tint, for actions that move several items at once.
   * Merged with `highlightedItemToken`.
   */
  highlightedItemTokens?: string[];
  /**
   * Rendered inside the scrollable area below the line items and
   * recommendations, so an extra section scrolls with the cart rather than
   * competing with the pinned totals footer.
   */
  belowItemsSlot?: React.ReactNode;
  /** Optional sticky notice rendered between the cart header and line items. */
  itemsNoticeSlot?: React.ReactNode;
  /** Optional notice rendered between the scroll area and pinned totals footer. */
  footerNoticeSlot?: React.ReactNode;
  /** Overrides the copy shown in place of the line items when `items` is empty. */
  emptyItemsMessage?: React.ReactNode;
  /** Exposes the cart's own scroll container so callers can scroll it without moving the page. */
  scrollAreaRef?: React.Ref<HTMLDivElement>;
  /** When provided, the brand name links here (e.g. to that brand's storefront page) instead of rendering as plain text. */
  brandHref?: string;
  /** Extra className applied to every line-item/recommendation `<img>` (e.g. a tint overlay). */
  imageOverlayClassName?: string;
}

/**
 * The retailer-facing brand side cart: a sticky right rail with the brand name,
 * order-minimum progress, editable line items, a "Complete your cart"
 * recommendation list, and a totals footer with View cart / Checkout. Checkout
 * is disabled until the order minimum is reached. Faithful to the production
 * brand storefront (b_tthhaz7z5x).
 */
export default function BrandSideCart({
  brandName,
  minimum,
  items,
  recommendations = [],
  taxRate = 0.06,
  onChangeQuantity,
  onRemove,
  onMoveToSavedForLater,
  onAddRecommendation,
  onViewCart,
  onCheckout,
  className,
  onBack,
  onClose,
  highlightedItemToken,
  highlightedItemTokens,
  belowItemsSlot,
  itemsNoticeSlot,
  footerNoticeSlot,
  emptyItemsMessage,
  scrollAreaRef,
  brandHref,
  imageOverlayClassName,
}: BrandSideCartProps) {
  const subtotal = items.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );
  const remaining = Math.max(0, minimum - subtotal);
  const progress = minimum > 0 ? Math.min(1, subtotal / minimum) : 1;
  const minimumReached = remaining <= 0 && items.length > 0;
  const salesTax = subtotal * taxRate;
  const highlightedTokens = new Set([
    ...(highlightedItemTokens ?? []),
    ...(highlightedItemToken === undefined ? [] : [highlightedItemToken]),
  ]);
  const backLabel = usePlaygroundLocalMsg("Back to all brands");
  const closeLabel = usePlaygroundLocalMsg("Close cart");
  const noMinimumLabel = usePlaygroundLocalMsg("No minimum");
  const viewCartLabel = usePlaygroundLocalMsg("View cart");
  const checkoutLabel = usePlaygroundLocalMsg("Checkout");

  return (
    <div
      className={cn(
        "bg-fs-surface-primary border-fs-border-muted flex h-full w-[320px] shrink-0 flex-col border-l xl:w-[360px]",
        className
      )}
    >
      {/* Scrollable area */}
      <div
        ref={scrollAreaRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        {/* Header */}
        <div className="p-6">
          <div className="mb-1 flex items-center gap-2">
            {onBack ? (
              <button
                type="button"
                aria-label={backLabel}
                onClick={onBack}
                className="text-fs-text-primary -ml-1 flex h-6 w-6 shrink-0 items-center justify-center"
              >
                <BackIcon />
              </button>
            ) : null}
            {brandHref ? (
              <Link
                to={brandHref}
                className="type-fs-subheading-medium text-fs-text-primary min-w-0 flex-1 truncate hover:underline"
              >
                {brandName}
              </Link>
            ) : (
              <h4 className="type-fs-subheading-medium text-fs-text-primary min-w-0 flex-1 truncate">
                {brandName}
              </h4>
            )}
            {onClose ? (
              <button
                type="button"
                aria-label={closeLabel}
                onClick={onClose}
                className="text-fs-text-primary flex h-6 w-6 shrink-0 items-center justify-center"
              >
                <CloseIcon />
              </button>
            ) : null}
          </div>
          <p className="type-fs-paragraph text-fs-text-primary pb-2">
            {minimum === 0
              ? noMinimumLabel
              : `${formatMoney(minimum)} minimum${
                  minimumReached ? "" : ` • ${formatMoney(remaining)} to go`
                }`}
          </p>
          <ProgressBar progress={progress} variant="success" showIcon />
        </div>

        {itemsNoticeSlot}

        {/* Line items */}
        <div className="flex flex-col gap-4 px-6 pb-2">
          {items.map((item) => (
            <SideCartLine
              key={item.token}
              item={item}
              isHighlighted={highlightedTokens.has(item.token)}
              onChangeQuantity={onChangeQuantity}
              onMoveToSavedForLater={onMoveToSavedForLater}
              onRemove={onRemove}
              imageOverlayClassName={imageOverlayClassName}
            />
          ))}
          {items.length === 0 ? (
            <p className="type-fs-paragraph text-fs-text-subdued py-4">
              {emptyItemsMessage ?? "Your cart is empty."}
            </p>
          ) : null}
        </div>

        {/* Complete your cart */}
        {recommendations.length > 0 ? (
          <div className="mt-6">
            <div className="px-0">
              <hr className="border-fs-border-muted border-t" />
            </div>
            <p className="type-fs-paragraph-medium text-fs-text-primary px-6 pt-6 pb-4">
              Complete your cart
            </p>
            <div className="flex flex-col gap-4 px-6 pb-6">
              {recommendations.map((recommendation) => (
                <RecommendationCard
                  key={recommendation.token}
                  recommendation={recommendation}
                  onAdd={onAddRecommendation}
                  imageOverlayClassName={imageOverlayClassName}
                />
              ))}
            </div>
          </div>
        ) : null}

        {belowItemsSlot}
      </div>

      {/* Footer. The shadow sits on the wrapper so it falls above
      `footerNoticeSlot` instead of across it. */}
      <div className="z-10 shadow-[0_-5px_10px_-5px_rgba(0,0,0,0.2)]">
        {footerNoticeSlot}

        <div className="flex flex-col gap-3 p-4">
          <div className="flex flex-col gap-1">
            <div className="flex flex-row justify-between">
              <p className="type-fs-paragraph-medium text-fs-text-primary">
                Item total
              </p>
              <p className="type-fs-paragraph-medium text-fs-text-primary">
                {formatMoney(subtotal)}
              </p>
            </div>
            <div className="flex flex-row justify-between">
              <p className="type-fs-paragraph text-fs-text-primary">
                Sales tax
              </p>
              <p className="type-fs-paragraph text-fs-text-primary">
                {formatMoney(salesTax)}
              </p>
            </div>
          </div>

          {/* Production pairs the two CTAs in one row and only reveals
          Checkout once the brand minimum is met. */}
          <div className="flex w-full gap-2">
            <div className="min-w-0 flex-1">
              <Button
                fullWidth
                onClick={onViewCart}
                size="small"
                variant="secondary"
              >
                {viewCartLabel}
              </Button>
            </div>
            {minimumReached ? (
              <div className="min-w-0 flex-1">
                <Button
                  fullWidth
                  onClick={onCheckout}
                  size="small"
                  variant="primary"
                >
                  {checkoutLabel}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function SideCartLine({
  item,
  isHighlighted,
  onChangeQuantity,
  onMoveToSavedForLater,
  onRemove,
  imageOverlayClassName,
}: {
  item: BrandSideCartItem;
  isHighlighted?: boolean;
  onChangeQuantity?: (token: string, quantity: number) => void;
  onMoveToSavedForLater?: (token: string) => void;
  onRemove?: (token: string) => void;
  imageOverlayClassName?: string;
}) {
  const { src, onError } = usePlaygroundImageWithFallback(item.imageUrl);
  const saveForLaterLabel = usePlaygroundLocalMsg("Save for later");

  return (
    <div
      className={cn(
        // Padding stays on in both states so only the color animates — the row
        // must not jump when the highlight fades out. The tint arrives fast and
        // leaves slowly, so the fade reads as the row settling back down.
        "-mx-2 flex items-center rounded px-2 py-1.5 transition-colors",
        isHighlighted
          ? "bg-fs-message-surface-warning duration-150 ease-out"
          : "bg-transparent duration-1000 ease-in-out"
      )}
    >
      <picture className="block size-16 shrink-0 self-start overflow-hidden rounded">
        <img
          src={src}
          onError={onError}
          alt={item.name}
          referrerPolicy="no-referrer"
          className={cn("h-full w-full object-cover", imageOverlayClassName)}
        />
      </picture>

      <div className="ml-4 flex min-w-0 flex-1 flex-col">
        <p className="type-fs-paragraph-medium text-fs-text-primary truncate">
          {item.name}
        </p>
        <p className="type-fs-paragraph text-fs-text-primary">
          {formatMoney(item.unitPrice * item.quantity)}
        </p>
        {item.canMoveToSavedForLater ? (
          <button
            className="type-fs-label text-fs-text-active w-fit cursor-pointer self-start text-left underline"
            onClick={() => onMoveToSavedForLater?.(item.token)}
            type="button"
          >
            {saveForLaterLabel}
          </button>
        ) : null}
      </div>

      <div className="ml-4 flex items-center gap-3">
        <div className="border-fs-border-muted flex items-center rounded border px-2 py-1.5">
          <input
            aria-label={`Quantity for ${item.name}`}
            inputMode="numeric"
            value={item.quantity}
            onChange={(event) => {
              const next = parseInt(event.target.value, 10);
              onChangeQuantity?.(item.token, Number.isNaN(next) ? 0 : next);
            }}
            className="type-fs-paragraph text-fs-text-primary w-7 bg-transparent text-center focus-visible:outline-none"
          />
        </div>
        <button
          type="button"
          aria-label={`Remove ${item.name} from cart`}
          onClick={() => onRemove?.(item.token)}
          className="text-fs-text-primary flex h-6 w-6 shrink-0 items-center justify-center"
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  );
}

function RecommendationCard({
  recommendation,
  onAdd,
  imageOverlayClassName,
}: {
  recommendation: BrandSideCartRecommendation;
  onAdd?: (token: string) => void;
  imageOverlayClassName?: string;
}) {
  const { src, onError } = usePlaygroundImageWithFallback(
    recommendation.imageUrl
  );

  return (
    <div className="border-fs-border-muted hover:border-fs-action-border-default rounded border p-3">
      <div className="relative flex w-full items-start gap-3">
        <picture className="block size-16 shrink-0 overflow-hidden rounded">
          <img
            src={src}
            onError={onError}
            alt={recommendation.name}
            referrerPolicy="no-referrer"
            className={cn("h-full w-full object-cover", imageOverlayClassName)}
          />
        </picture>

        <div className="flex min-w-0 flex-col">
          {recommendation.badge ? (
            <p className="type-fs-paragraph-medium text-fs-message-text-sale">
              {recommendation.badge}
            </p>
          ) : null}
          <p className="type-fs-paragraph-medium text-fs-text-primary line-clamp-1">
            {recommendation.name}
          </p>
          <div className="flex flex-row items-baseline gap-2">
            <span className="type-fs-paragraph text-fs-text-primary">
              {recommendation.price}
            </span>
            {recommendation.msrp ? (
              <span className="type-fs-paragraph text-fs-text-subdued">
                {recommendation.msrp}
              </span>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          aria-label={`Add ${recommendation.name} to cart`}
          onClick={() => onAdd?.(recommendation.token)}
          className="border-fs-border-muted bg-fs-surface-primary text-fs-text-active hover:border-fs-action-border-active hover:bg-fs-surface-secondary absolute right-0 bottom-0 flex size-8 items-center justify-center rounded-2xl border transition-all duration-200"
        >
          <PlusIcon />
        </button>
      </div>
    </div>
  );
}

function formatMoney(value: number): string {
  const fixed = value.toFixed(2);
  return fixed.endsWith(".00") ? `$${Math.round(value)}` : `$${fixed}`;
}

function BackIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1.714 6h20.572M7.714 6v-.857a4.286 4.286 0 1 1 8.572 0V6M9.429 9.429v9.428m5.143-9.428v9.428M4.286 6h15.429v15.429A1.715 1.715 0 0 1 18 23.143H6a1.715 1.715 0 0 1-1.714-1.714z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 1.714v20.572M1.714 12h20.572" />
    </svg>
  );
}
